/**
 * ClinicalEvent — shared event model for annotations, qEEG markers,
 * artifacts, imported events, and future AI detections.
 *
 * Maps onto the canonical `eeg_event` store (`sourceKind` + `eventTypeCode`
 * + `payloadJson.kind` discriminator).
 */

export type ClinicalEventSourceKind =
  | "manual"
  | "algorithm"
  | "import"
  | "system";

export type ClinicalEventStatus = "active" | "suppressed" | "deleted";

export type ClinicalEventKind =
  | "annotation"
  | "qeeg_marker"
  | "artifact"
  | "bookmark"
  | "clip"
  | "medication"
  | "vital"
  | "video"
  | "upload_milestone"
  | "conversion_milestone"
  | "report_action"
  | "handoff_note"
  | "ai_detection"
  | "imported";

export type ClinicalEvent = {
  eventId: string;
  studyId: string;
  recordingId: string;
  kind: ClinicalEventKind;
  sourceKind: ClinicalEventSourceKind;
  eventTypeCode: string;
  eventLabel: string;
  startTimeSeconds: number;
  endTimeSeconds?: number | null;
  channels: string[];
  confidenceScore?: number | null;
  severityScore?: number | null;
  notes?: string | null;
  eventStatus: ClinicalEventStatus;
  payload: Record<string, unknown>;
  sourceAnnotationId?: string | null;
  sourceRef?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type ClinicalTimelineRowFilter =
  | ClinicalEventKind
  | "all";

export type ClinicalTimelineDensityMode = "compact" | "normal" | "expanded";

export type ClinicalTimelineBucket = {
  start: number;
  end: number;
  total: number;
  byType: Record<string, number>;
  byKind: Record<string, number>;
};

export type ClinicalTimelinePayload = {
  recordingId: string;
  start: number;
  end: number;
  bucketSec: number;
  events: ClinicalEvent[];
  buckets: ClinicalTimelineBucket[];
  densityMode: ClinicalTimelineDensityMode;
  filters: ClinicalEventKind[];
};

export function resolveClinicalEventKind(
  eventTypeCode: string,
  payload: Record<string, unknown> = {},
): ClinicalEventKind {
  const explicit = payload.kind;
  if (
    typeof explicit === "string" &&
    [
      "annotation",
      "qeeg_marker",
      "artifact",
      "bookmark",
      "clip",
      "medication",
      "vital",
      "video",
      "upload_milestone",
      "conversion_milestone",
      "report_action",
      "handoff_note",
      "ai_detection",
      "imported",
    ].includes(explicit)
  ) {
    return explicit as ClinicalEventKind;
  }

  const code = eventTypeCode.toLowerCase();
  if (code.includes("artifact") || code.startsWith("emg") || code.startsWith("eog")) {
    return "artifact";
  }
  if (code.includes("bookmark")) return "bookmark";
  if (code.includes("clip")) return "clip";
  if (code.includes("medication") || code.includes("intervention")) {
    return "medication";
  }
  if (code.includes("vital")) return "vital";
  if (code.includes("video")) return "video";
  if (code.includes("upload")) return "upload_milestone";
  if (code.includes("conversion")) return "conversion_milestone";
  if (code.includes("report")) return "report_action";
  if (code.includes("handoff")) return "handoff_note";
  if (code.includes("qeeg") || code.includes("asymmetry") || code.includes("suppression")) {
    return "qeeg_marker";
  }
  if (code.includes("spike") || code.includes("seizure") || code.includes("ai_")) {
    return "ai_detection";
  }
  if (code.includes("import")) return "imported";
  return "annotation";
}

export function toClinicalEvent(row: {
  eventId: string;
  studyId: string;
  recordingId: string;
  sourceKind: ClinicalEventSourceKind;
  eventTypeCode: string;
  eventLabel: string;
  startTimeSeconds: number;
  endTimeSeconds?: number | null;
  channels?: string[] | null;
  confidenceScore?: number | null;
  severityScore?: number | null;
  notes?: string | null;
  eventStatus?: ClinicalEventStatus;
  payloadJson?: Record<string, unknown> | null;
  sourceAnnotationId?: string | null;
  sourceRef?: string | null;
  createdAt?: string;
  updatedAt?: string;
}): ClinicalEvent {
  const payload = row.payloadJson ?? {};
  return {
    eventId: row.eventId,
    studyId: row.studyId,
    recordingId: row.recordingId,
    kind: resolveClinicalEventKind(row.eventTypeCode, payload),
    sourceKind: row.sourceKind,
    eventTypeCode: row.eventTypeCode,
    eventLabel: row.eventLabel,
    startTimeSeconds: row.startTimeSeconds,
    endTimeSeconds: row.endTimeSeconds ?? null,
    channels: row.channels ?? [],
    confidenceScore: row.confidenceScore ?? null,
    severityScore: row.severityScore ?? null,
    notes: row.notes ?? null,
    eventStatus: row.eventStatus ?? "active",
    payload,
    sourceAnnotationId: row.sourceAnnotationId ?? null,
    sourceRef: row.sourceRef ?? null,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  };
}
